import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Star } from "lucide-react";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";

function StarRating({ value }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < value ? "fill-accent text-accent" : "text-text-muted"}`}
        />
      ))}
    </div>
  );
}

export default function TestimonialsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "Testimonials · Admin";
  }, []);

  const { list, remove } = useResource("testimonials", { toastLabel: "testimonial" });
  const { data, isLoading } = list({
    page,
    limit: 20,
    sort: "sortOrder:asc",
    ...(search && { search }),
    ...(source && { source }),
  });

  useEffect(() => {
    setPage(1);
  }, [search, source]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">About</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Testimonials</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} review{data?.meta?.total === 1 ? "" : "s"} total
          </p>
        </div>
        <Link to="/testimonials/new">
          <Button leftIcon={<Plus className="h-4 w-4" />}>New testimonial</Button>
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by customer name…"
          filters={
            <Select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="min-w-[150px]"
            >
              <option value="">Any source</option>
              <option value="google">Google</option>
              <option value="productreview">ProductReview</option>
              <option value="direct">Direct</option>
            </Select>
          }
        />

        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          onRowClick={(row) => navigate(`/testimonials/${row._id}`)}
          columns={[
            {
              key: "customerName",
              label: "Customer",
              render: (r) => <strong>{r.customerName}</strong>,
            },
            {
              key: "rating",
              label: "Rating",
              render: (r) => <StarRating value={r.rating ?? 5} />,
            },
            {
              key: "text",
              label: "Quote",
              render: (r) => (
                <span className="text-text-secondary">
                  {r.text ? r.text.slice(0, 80) + (r.text.length > 80 ? "…" : "") : ""}
                </span>
              ),
            },
            {
              key: "source",
              label: "Source",
              render: (r) => <Badge tone="info">{r.source}</Badge>,
            },
            {
              key: "isFeatured",
              label: "Featured",
              render: (r) => (r.isFeatured ? <Badge tone="accent">featured</Badge> : "—"),
            },
          ]}
          actions={(row) => (
            <RowActions
              onEdit={() => navigate(`/testimonials/${row._id}`)}
              onDelete={() => setConfirmDelete(row)}
            />
          )}
          empty={{
            title: "No testimonials yet",
            description: "Add your first testimonial to populate the homepage marquee.",
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete ${confirmDelete?.customerName}'s review?`}
        description="The testimonial will be removed from the homepage immediately."
        confirmLabel="Delete testimonial"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}
